import { useCallback, useEffect, useRef, useState } from "react";
import {
  CalendarDays,
  Camera,
  CreditCard,
  Download,
  Heart,
  Image,
  Images,
  LayoutDashboard,
  LoaderCircle,
  LockKeyhole,
  LogOut,
  MapPin,
  MessageCircle,
  RefreshCw,
  User,
} from "lucide-react";
import {
  downloadPhoto,
  exchangeAuthHandoff,
  loadEventSubscription,
  loadGalleryPage,
  loadPlans,
  loadWorkspace,
  requestOtp,
  setFavorite,
  signOut,
  startEventCheckout,
  verifyOtp,
} from "./api";
import { PaymentActions, ProfileView, SupportView } from "./AccountViews";
import { getClientViewFromUrl, navigateClientView } from "./routes";
import { captureOtpFill, showDevOtpFill } from "./otp-fill";

const navigation = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "events", label: "My events", icon: CalendarDays },
  { id: "subscription", label: "Subscription", icon: CreditCard },
  { id: "gallery", label: "Gallery", icon: Images },
  { id: "profile", label: "Profile", icon: User },
  { id: "support", label: "Support", icon: MessageCircle },
];

function captureAuthHandoff(
  location = globalThis.location,
  history = globalThis.history,
) {
  const fragment = new URLSearchParams(
    String(location?.hash || "").replace(/^#/, ""),
  );
  if (fragment.get("workspace") !== "client") return "";
  const token = fragment.get("auth_handoff") || "";
  if (!token) return "";
  fragment.delete("auth_handoff");
  fragment.delete("workspace");
  const nextHash = fragment.toString();
  history?.replaceState?.(
    null,
    "",
    `${location.pathname || "/"}${location.search || ""}${nextHash ? `#${nextHash}` : ""}`,
  );
  return token;
}

function App() {
  const [otpFill, setOtpFill] = useState(() => captureOtpFill("user"));
  const handoffTokenRef = useRef(captureAuthHandoff());
  const [view, setViewState] = useState(() => getClientViewFromUrl());
  const [checkoutReturn] = useState(() =>
    globalThis.location?.search?.includes("session_id=")
      ? "Checkout returned. Payment access will update only after Stripe confirms it to PhotoX."
      : "",
  );
  const [workspace, setWorkspace] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState("");
  const galleryEventRef = useRef("");
  const [photos, setPhotos] = useState([]);
  const [galleryPage, setGalleryPage] = useState(1);
  const [galleryPages, setGalleryPages] = useState(1);
  const [galleryLoadingMore, setGalleryLoadingMore] = useState(false);
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [authRequired, setAuthRequired] = useState(false);
  const [otpEmail, setOtpEmail] = useState(otpFill?.email || "");
  const [devCode, setDevCode] = useState("");
  const [error, setError] = useState("");

  const setView = useCallback((nextView) => {
    setViewState(nextView);
    navigateClientView(nextView);
  }, []);

  useEffect(() => {
    function handleOtpHash() {
      const fill = captureOtpFill("user");
      if (fill) {
        setOtpFill(fill);
        setOtpEmail(fill.email);
        setAuthRequired(true);
      }
    }
    window.addEventListener("hashchange", handleOtpHash);
    window.addEventListener("focus", handleOtpHash);
    return () => {
      window.removeEventListener("hashchange", handleOtpHash);
      window.removeEventListener("focus", handleOtpHash);
    };
  }, []);

  useEffect(() => {
    function handlePop() {
      setViewState(getClientViewFromUrl());
    }
    window.addEventListener("popstate", handlePop);
    return () => window.removeEventListener("popstate", handlePop);
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      if (handoffTokenRef.current) {
        const token = handoffTokenRef.current;
        handoffTokenRef.current = "";
        await exchangeAuthHandoff(token);
      }
      const data = await loadWorkspace();
      setWorkspace(data);
      setAuthRequired(false);
      setSelectedEventId(
        (current) =>
          data.events.some((event) => event.id === current)
            ? current
            : data.events[0]?.id || "",
      );
    } catch (requestError) {
      setWorkspace(null);
      setAuthRequired(
        requestError.status === 401 || requestError.status === 403,
      );
      setError(
        requestError.status === 401 || requestError.status === 403
          ? "Sign in with the email used for your photographer request."
          : requestError.message,
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);
  useEffect(() => {
    galleryEventRef.current = selectedEventId;
    if (!selectedEventId) {
      setPhotos([]);
      setGalleryPage(1);
      setGalleryPages(1);
      setSubscription(null);
      return undefined;
    }
    setPhotos([]);
    setGalleryPage(1);
    setGalleryPages(1);
    const controller = new AbortController();
    Promise.all([
      loadGalleryPage(selectedEventId, "visible", 1, controller.signal),
      loadEventSubscription(selectedEventId, controller.signal).catch(
        (requestError) => {
          if (requestError.status === 403) return null;
          throw requestError;
        },
      ),
    ])
      .then(([nextPhotos, nextSubscription]) => {
        if (galleryEventRef.current !== selectedEventId) return;
        setPhotos(nextPhotos.photos);
        setGalleryPages(nextPhotos.pagination.pages);
        setSubscription(nextSubscription);
      })
      .catch((requestError) => {
        if (requestError.name !== "AbortError") setError(requestError.message);
      });
    return () => controller.abort();
  }, [selectedEventId]);

  useEffect(() => {
    if (!workspace || view !== "subscription") return;
    loadPlans()
      .then(setPlans)
      .catch((requestError) => setError(requestError.message));
  }, [workspace, view]);

  async function sendCode(email) {
    setBusy(true);
    setError("");
    setDevCode("");
    try {
      const result = await requestOtp(email);
      setOtpEmail(email);
      if (result?.devCode) setDevCode(result.devCode);
      return true;
    } catch (requestError) {
      setError(requestError.message);
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function authenticate(code) {
    setBusy(true);
    setError("");
    try {
      const result = await verifyOtp(otpEmail, code);
      if (result.routing?.kind === "no-invitation") {
        setAuthRequired(true);
        setError(
          result.routing.message ||
            "This email is not linked to a Client photographer request.",
        );
        return;
      }
      await refresh();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  async function logout() {
    await signOut().catch(() => undefined);
    setWorkspace(null);
    setAuthRequired(true);
    setSelectedEventId("");
  }

  async function toggleFavorite(photo) {
    const favorite = !photo.favorite;
    try {
      await setFavorite(selectedEventId, photo.id, favorite);
      setPhotos((items) =>
        items.map((item) =>
          item.id === photo.id ? { ...item, favorite } : item,
        ),
      );
    } catch (requestError) {
      setError(requestError.message);
    }
  }

  async function checkout(planCode) {
    if (!selectedEventId) return;
    setBusy(true);
    setError("");
    try {
      const nextCheckout = await startEventCheckout(selectedEventId, planCode);
      window.location.assign(nextCheckout.url);
    } catch (requestError) {
      setError(requestError.message);
      setBusy(false);
    }
  }

  async function loadMoreGallery() {
    if (!selectedEventId || galleryLoadingMore || galleryPage >= galleryPages)
      return;
    const requestedEvent = selectedEventId;
    setGalleryLoadingMore(true);
    try {
      const next = await loadGalleryPage(
        requestedEvent,
        "visible",
        galleryPage + 1,
      );
      if (galleryEventRef.current !== requestedEvent) return;
      setPhotos((current) => [
        ...current,
        ...next.photos.filter(
          (photo) => !current.some((item) => item.id === photo.id),
        ),
      ]);
      setGalleryPage(next.pagination.page);
      setGalleryPages(next.pagination.pages);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setGalleryLoadingMore(false);
    }
  }

  async function download(photo) {
    try {
      const delivery = await downloadPhoto(selectedEventId, photo.id);
      if (!delivery?.url)
        throw new Error(
          "This photograph is not currently available for download.",
        );
      const link = document.createElement("a");
      link.href = delivery.url;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (requestError) {
      setError(requestError.message);
    }
  }

  if (loading)
    return <Status icon={LoaderCircle} spin title="Loading client workspace" />;
  if (!workspace && authRequired)
    return (
      <AuthScreen
        busy={busy}
        error={error}
        otpEmail={otpEmail}
        initialCode={otpFill?.code || ""}
        fillCode={otpFill?.code || ""}
        devCode={devCode}
        sendCode={sendCode}
        verify={authenticate}
        reset={() => {
          setOtpEmail("");
          setError("");
          setDevCode("");
          setOtpFill(null);
        }}
      />
    );
  if (!workspace)
    return (
      <Status
        icon={LogOut}
        title="Client workspace unavailable"
        message={error}
        action={refresh}
      />
    );

  const selectedEvent = workspace.events.find(
    (event) => event.id === selectedEventId,
  );
  const displayName = workspace.user.displayName || workspace.user.email;
  return (
    <main className="client-shell min-h-screen bg-canvas font-sans text-ink">
      <aside className="client-sidebar fixed inset-y-0 left-0 z-40 hidden w-64 flex-col bg-ink px-5 py-7 text-white lg:flex">
        <Brand />
        <div className="mb-7 rounded-2xl border border-white/10 bg-white/[.06] p-4">
          <small className="text-white/45">Signed-in account</small>
          <strong className="mt-2 block">{displayName}</strong>
        </div>
        <nav className="space-y-1" aria-label="Client primary navigation">
          {navigation.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setView(id)}
              aria-current={view === id ? "page" : undefined}
              className={`flex min-h-12 w-full items-center gap-3 rounded-xl px-3 text-sm font-semibold ${view === id ? "bg-white/10 text-white" : "text-white/50"}`}
            >
              <Icon size={18} />
              {label}
            </button>
          ))}
        </nav>
        <button
          onClick={logout}
          className="mt-auto flex items-center gap-2 text-sm text-white/60"
        >
          <LogOut size={17} />
          Sign out
        </button>
      </aside>
      <section className="min-w-0 lg:ml-64">
        <header className="flex h-[70px] items-center border-b border-[#e8e1d8] bg-paper px-5">
          <div className="lg:hidden">
            <Brand compact />
          </div>
          <span className="ml-auto text-xs font-semibold text-slate-500">
            Your event workspace
          </span>
          <button
            onClick={refresh}
            className="ml-3 grid h-10 w-10 place-items-center rounded-full border border-[#e8e1d8] bg-white"
            aria-label="Refresh"
          >
            <RefreshCw size={17} />
          </button>
        </header>
        <div className="mx-auto max-w-[1480px] px-4 pb-28 pt-8 sm:px-7">
          {error && <ErrorNotice message={error} />}
          {checkoutReturn && view === "subscription" && (
            <div
              className="mb-5 rounded-xl bg-amber-50 p-4 text-sm text-amber-900"
              role="status"
            >
              {checkoutReturn}
            </div>
          )}
          {view === "overview" && (
            <Overview
              user={workspace.user}
              events={workspace.events}
              photos={photos}
              openEvents={() => setView("events")}
            />
          )}
          {view === "events" && (
            <Events
              events={workspace.events}
              select={(id, destination = "gallery") => {
                setSelectedEventId(id);
                setView(destination);
              }}
            />
          )}
          {view === "subscription" && (
            <>
              <Subscription
                event={selectedEvent}
                events={workspace.events}
                eventId={selectedEventId}
                selectEvent={setSelectedEventId}
                subscription={subscription}
                plans={plans}
                busy={busy}
                checkout={checkout}
              />
              <PaymentActions
                eventId={selectedEventId}
                subscription={subscription}
                onError={setError}
              />
            </>
          )}
          {view === "gallery" && (
            <Gallery
              event={selectedEvent}
              photos={photos}
              events={workspace.events}
              selectedEventId={selectedEventId}
              selectEvent={setSelectedEventId}
              toggleFavorite={toggleFavorite}
              download={download}
              loadingMore={galleryLoadingMore}
              hasMore={galleryPage < galleryPages}
              onLoadMore={loadMoreGallery}
            />
          )}
          {view === "profile" && (
            <ProfileView
              user={workspace.user}
              onUser={(user) =>
                setWorkspace((current) => ({ ...current, user }))
              }
              onError={setError}
            />
          )}
          {view === "support" && (
            <SupportView events={workspace.events} onError={setError} />
          )}
        </div>
      </section>
      <nav
        aria-label="Client mobile navigation"
        className="fixed inset-x-2 bottom-2 z-50 flex overflow-x-auto rounded-2xl border border-[#e8e1d8] bg-paper/95 p-1.5 shadow-premium lg:hidden"
      >
        {navigation.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setView(id)}
            aria-current={view === id ? "page" : undefined}
            className={`flex min-w-[65px] flex-1 flex-col items-center gap-1 rounded-xl py-2 text-xs font-bold ${view === id ? "bg-ink text-white" : "text-slate-500"}`}
          >
            <Icon size={18} />
            {label}
          </button>
        ))}
      </nav>
    </main>
  );
}

function AuthScreen({
  busy,
  error,
  otpEmail,
  initialCode,
  fillCode,
  devCode,
  sendCode,
  verify,
  reset,
}) {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState(initialCode || fillCode || "");
  const awaitingCode = Boolean(otpEmail);

  useEffect(() => {
    if (fillCode) {
      setCode(fillCode);
    }
  }, [fillCode]);

  return (
    <main className="grid min-h-screen place-items-center bg-canvas p-5">
      <form
        className="w-full max-w-md rounded-3xl border border-[#e8e1d8] bg-paper p-8 shadow-premium"
        onSubmit={async (event) => {
          event.preventDefault();
          if (awaitingCode) await verify(code);
          else await sendCode(email);
        }}
      >
        <Brand />
        <p className="eyebrow">Event client portal</p>
        <h1 className="text-4xl font-extrabold">
          {awaitingCode ? "Check your email" : "Sign in with email"}
        </h1>
        <p className="text-sm text-slate-500">
          {awaitingCode
            ? `Enter the one-time code sent to ${otpEmail}.`
            : "We’ll email a secure one-time code—no password needed."}
        </p>
        {error && <ErrorNotice message={error} />}
        {fillCode && awaitingCode && (
          <button
            type="button"
            className="mt-3 flex w-full cursor-pointer items-center justify-between rounded-xl border border-dashed border-teal-500 bg-teal-50 px-4 py-2.5 text-xs text-teal-800"
            onClick={() => setCode(fillCode)}
            title="Tap to fill OTP code from magic link"
          >
            <span>
              Magic link OTP: <strong>{fillCode}</strong>
            </span>
            <span className="text-xs font-semibold text-teal-700">Tap to fill</span>
          </button>
        )}
        {showDevOtpFill(devCode) && !fillCode && awaitingCode && (
          <button
            type="button"
            className="mt-3 flex w-full cursor-pointer items-center justify-between rounded-xl border border-dashed border-teal-500 bg-teal-50 px-4 py-2.5 text-xs text-teal-800"
            onClick={() => {
              setCode(devCode);
            }}
            title="Tap to fill the same OTP emailed by SES (testing only)"
          >
            <span>
              Testing OTP: <strong>{devCode}</strong>
            </span>
            <span className="text-xs font-semibold text-teal-700">
              {code === devCode ? "✓ Filled" : "Tap to fill"}
            </span>
          </button>
        )}
        {awaitingCode ? (
          <AuthField
            label="6-digit OTP"
            value={code}
            onChange={(value) => setCode(value.replace(/\D/g, "").slice(0, 6))}
            autoComplete="one-time-code"
            inputMode="numeric"
          />
        ) : (
          <AuthField
            label="Email"
            type="email"
            value={email}
            onChange={setEmail}
            autoComplete="email"
          />
        )}
        <button
          disabled={busy || (awaitingCode && code.length !== 6)}
          className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-ink px-5 py-3 font-bold text-white"
        >
          {busy ? (
            <LoaderCircle className="animate-spin" size={18} />
          ) : (
            <LockKeyhole size={18} />
          )}
          {awaitingCode ? "Verify & sign in" : "Send OTP"}
        </button>
        {awaitingCode && (
          <button
            type="button"
            className="mt-4 w-full text-sm font-bold text-coral-dark"
            onClick={reset}
          >
            Use a different email
          </button>
        )}
      </form>
    </main>
  );
}
function AuthField({
  label,
  type = "text",
  value,
  onChange,
  autoComplete,
  inputMode,
}) {
  return (
    <label className="mt-4 block text-sm font-bold">
      {label}
      <input
        className="mt-2 w-full rounded-xl border border-[#e8e1d8] bg-white px-4 py-3 font-normal"
        required
        type={type}
        value={value}
        inputMode={inputMode}
        autoComplete={autoComplete}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}
function Brand({ compact = false }) {
  return (
    <div
      className={`${compact ? "" : "mb-9"} flex items-center text-xl font-extrabold`}
    >
      <span className="mr-2 grid h-9 w-9 place-items-center rounded-xl bg-coral text-white">
        <Image size={19} />
      </span>
      PhotoX
    </div>
  );
}
function Heading({ eyebrow, title, text }) {
  return (
    <div className="mb-7">
      <p className="mb-2 text-xs font-extrabold uppercase tracking-[.17em] text-coral-dark">
        {eyebrow}
      </p>
      <h1 className="mb-2 text-4xl font-extrabold sm:text-5xl">{title}</h1>
      <p className="m-0 text-sm text-slate-500">{text}</p>
    </div>
  );
}
function Overview({ user, events, photos, openEvents }) {
  return (
    <>
      <Heading
        eyebrow="Client workspace"
        title={`Welcome, ${user.displayName || user.email}`}
        text="Track your event setup, plan status, and private gallery in one place."
      />
      <div className="grid gap-4 sm:grid-cols-3">
        <Metric label="Accessible events" value={events.length} />
        <Metric label="Selected gallery photos" value={photos.length} />
        <Metric
          label="Favourites"
          value={photos.filter((photo) => photo.favorite).length}
        />
      </div>
      <button
        onClick={openEvents}
        className="mt-6 rounded-xl bg-ink px-5 py-3 text-xs font-bold text-white"
      >
        View events
      </button>
    </>
  );
}
function Metric({ label, value }) {
  return (
    <article className="rounded-3xl border border-[#e8e1d8] bg-paper p-6 shadow-card">
      <small className="text-slate-500">{label}</small>
      <strong className="mt-2 block text-3xl">{value}</strong>
    </article>
  );
}
function Events({ events, select }) {
  return (
    <>
      <Heading
        eyebrow="Your celebrations"
        title="My events"
        text="Confirmed booking workspaces and events assigned to your account. Publishing is managed by the PhotoX team."
      />
      {events.length ? (
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {events.map((event) => {
            const photographers = event.assignedPhotographers || [];
            const hasPhotographer = photographers.length > 0;
            const isAwaitingPhotographer =
              event.status === "scheduled" && !hasPhotographer;
            const statusLabel = isAwaitingPhotographer
              ? "awaiting photographer"
              : event.status;
            const statusClass = isAwaitingPhotographer
              ? "status-review"
              : event.status === "live"
                ? "status-live"
                : "status-draft";
            return (
              <article
                key={event.id}
                className="rounded-3xl border border-[#e8e1d8] bg-paper p-6 shadow-card"
              >
                <span className={`status ${statusClass}`}>
                  {statusLabel}
                </span>
                <h2 className="mt-5 text-2xl font-extrabold">{event.name}</h2>
                <p className="flex items-center gap-2 text-xs text-slate-500">
                  <CalendarDays size={14} />
                  {formatDate(event.startsAt)}
                </p>
                <p className="flex items-center gap-2 text-xs text-slate-500">
                  <MapPin size={14} />
                  {event.venue?.city || "Location not set"}
                </p>
                <p className="flex items-center gap-2 text-xs text-slate-500">
                  <Camera size={14} />
                  {hasPhotographer
                    ? `Photographer: ${photographers.map((p) => p.displayName || p.email).join(", ")}`
                    : "Photographer: Matching by PhotoX team"}
                </p>
                {event.billingRequired &&
                event.status === "draft" &&
                !["active", "trialing"].includes(event.subscription?.status) ? (
                  <button
                    onClick={() => select(event.id, "subscription")}
                    className="mt-4 rounded-xl bg-coral px-4 py-3 text-xs font-bold text-white"
                  >
                    Choose plan
                  </button>
                ) : (
                  <button
                    onClick={() => select(event.id)}
                    className="mt-4 rounded-xl bg-ink px-4 py-3 text-xs font-bold text-white"
                  >
                    Open gallery
                  </button>
                )}
              </article>
            );
          })}
        </div>
      ) : (
        <Empty text="No paid event is available yet. An event appears here only after Stripe confirms the selected plan payment." />
      )}
    </>
  );
}
function Subscription({
  event,
  events,
  eventId,
  selectEvent,
  subscription,
  plans,
  busy,
  checkout,
}) {
  const active =
    subscription && ["active", "trialing"].includes(subscription.status);
  return (
    <>
      <Heading
        eyebrow="Event access plan"
        title="Subscription"
        text="Your selected fixed-price plan covers the event, photographer service, gallery, storage, and included features."
      />
      <label className="mb-5 block text-sm font-semibold text-slate-700">
        Select an event
        <select
          className="mt-2 block min-h-12 rounded-xl border border-[#e8e1d8] bg-white p-3"
          value={eventId}
          onChange={(item) => selectEvent(item.target.value)}
        >
          <option value="">Select an event</option>
          {events.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
      </label>
      {!event ? (
        <Empty text="Select a confirmed event to view its available plans." />
      ) : (
        <>
          {subscription && (
            <article className="mb-6 rounded-3xl border border-[#e8e1d8] bg-paper p-7">
              <span
                className={`status ${active ? "status-paid" : "status-error"}`}
              >
                {subscription.status}
              </span>
              <h2 className="mt-4 text-2xl font-extrabold">{event.name}</h2>
              <p className="text-sm text-slate-500">
                Plan: {subscription.planCode} · Current period ends{" "}
                {formatDate(subscription.currentPeriodEnd)}
              </p>
              <p className="text-sm text-slate-500">
                {formatBytes(subscription.entitlements?.storageBytes)} storage ·{" "}
                {subscription.entitlements?.maxPhotos ?? "Flexible"} photos
              </p>
              {active && (
                <p className="mt-3 text-xs font-semibold text-green-700">
                  Payment is confirmed and this fixed event plan is active. No
                  quote approval or additional photographer payment is required.
                </p>
              )}
            </article>
          )}
          {!active &&
            (plans.length ? (
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {plans.map((plan) => (
                  <article
                    key={plan.code}
                    className="rounded-3xl border border-[#e8e1d8] bg-paper p-7 shadow-card"
                  >
                    <CreditCard className="text-coral" />
                    <p className="mt-4 text-xs font-bold uppercase tracking-widest text-slate-400">
                      {plan.version
                        ? `Published version ${plan.version}`
                        : "Available plan"}
                    </p>
                    <h2 className="mt-2 text-2xl font-extrabold">
                      {plan.name}
                    </h2>
                    {Number.isInteger(plan.amountMinor) ? (
                      <strong className="mt-2 block text-xl">
                        {formatMoney(plan.amountMinor, plan.currency)}{" "}
                        <small className="text-xs text-slate-500">
                          /{" "}
                          {plan.billingInterval === "event"
                            ? "event"
                            : plan.billingInterval}
                        </small>
                      </strong>
                    ) : (
                      <p className="mt-2 text-xs text-slate-500">
                        Final price shown at secure Stripe checkout
                      </p>
                    )}
                    <p className="mt-3 text-sm text-slate-500">
                      {formatBytes(
                        plan.entitlements?.storageBytes ?? plan.storageBytes,
                      )}{" "}
                      storage · {plan.entitlements?.retentionDays || 90}{" "}
                      retention days
                    </p>
                    {plan.entitlements?.maxPhotos !== undefined && (
                      <p className="mt-1 text-xs text-slate-500">
                        Up to {plan.entitlements.maxPhotos} photographs ·{" "}
                        {plan.entitlements.maxGuests} guests
                      </p>
                    )}
                    {plan.entitlements?.features?.length > 0 && (
                      <ul className="mt-3 list-disc pl-4 text-sm leading-7 text-slate-500">
                        {plan.entitlements.features.map((feature) => (
                          <li key={feature}>{feature}</li>
                        ))}
                      </ul>
                    )}
                    {plan.policies?.cancellation && (
                      <p className="mt-4 text-xs text-slate-500">
                        Cancellation: {plan.policies.cancellation}
                      </p>
                    )}
                    <button
                      disabled={busy || !(plan.version || plan.legacy)}
                      onClick={() => checkout(plan.code)}
                      className="mt-5 min-h-12 rounded-xl bg-ink px-5 py-3 text-sm font-bold text-white disabled:opacity-50"
                    >
                      Continue to secure checkout
                    </button>
                  </article>
                ))}
              </div>
            ) : (
              <Empty text="No published plans are available yet. Please contact the PhotoX team." />
            ))}
        </>
      )}
    </>
  );
}

function Gallery({
  event,
  photos,
  events,
  selectedEventId,
  selectEvent,
  toggleFavorite,
  download,
  loadingMore,
  hasMore,
  onLoadMore,
}) {
  const [query, setQuery] = useState("");
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const visible = photos.filter(
    (photo) =>
      (!favoritesOnly || photo.favorite) &&
      (!query ||
        [photo.originalFilename, photo.caption, photo.kind].some((value) =>
          String(value || "")
            .toLowerCase()
            .includes(query.toLowerCase().trim()),
        )),
  );
  return (
    <section className="px-client-gallery">
      <Heading
        eyebrow={event?.name || "Your event gallery"}
        title="Your photographs"
        text="Browse your authorized event collection, save favourites and open originals when your plan allows."
      />
      <div className="px-gallery-toolbar">
        <label className="px-gallery-event-picker">
          Event
          <select
            value={selectedEventId}
            onChange={(change) => selectEvent(change.target.value)}
          >
            <option value="">Select an event</option>
            {events.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
        <label className="px-gallery-search">
          Search loaded photographs
          <input
            type="search"
            value={query}
            onChange={(change) => setQuery(change.target.value)}
            placeholder="Filename, caption or type"
          />
        </label>
        <button
          type="button"
          aria-pressed={favoritesOnly}
          onClick={() => setFavoritesOnly((value) => !value)}
          className={`px-gallery-favorite-filter ${favoritesOnly ? "is-active" : ""}`}
        >
          <Heart size={17} fill={favoritesOnly ? "currentColor" : "none"} />{" "}
          Favourites
        </button>
      </div>
      <p className="px-gallery-summary" role="status">
        {photos.length} loaded photographs{hasMore ? " · More available" : ""}
        {query || favoritesOnly ? ` · ${visible.length} matching` : ""}
      </p>
      {visible.length ? (
        <div className="px-client-photo-grid">
          {visible.map((photo) => (
            <article key={photo.id} className="px-client-photo-card">
              <div className="px-client-photo-image">
                {photo.mediaUrl ? (
                  <img
                    loading="lazy"
                    decoding="async"
                    src={photo.mediaUrl}
                    alt={
                      photo.caption || photo.originalFilename || "Event photo"
                    }
                  />
                ) : (
                  <div className="px-client-photo-placeholder">
                    <Images size={32} />
                    <span>Preview unavailable</span>
                  </div>
                )}
                <span className="px-photo-privacy">
                  {photo.privacy === "shared"
                    ? "Shared gallery"
                    : "Private access"}
                </span>
              </div>
              <div className="px-client-photo-content">
                <div className="px-client-photo-details">
                  <strong title={photo.originalFilename}>
                    {photo.originalFilename || "Untitled photograph"}
                  </strong>
                  <small>
                    {photo.kind} · {photo.status}
                  </small>
                </div>
                <div className="px-client-photo-actions">
                  <button
                    type="button"
                    onClick={() => toggleFavorite(photo)}
                    className={`px-photo-action ${photo.favorite ? "is-favorite" : ""}`}
                    aria-label={
                      photo.favorite ? "Remove favourite" : "Add favourite"
                    }
                    aria-pressed={Boolean(photo.favorite)}
                  >
                    <Heart
                      size={17}
                      fill={photo.favorite ? "currentColor" : "none"}
                    />
                  </button>
                  <button
                    type="button"
                    onClick={() => download(photo)}
                    disabled={
                      !photo.original?.available || photo.status !== "ready"
                    }
                    aria-label={`Open original ${photo.originalFilename || "photograph"}`}
                    className="px-photo-download"
                  >
                    <Download size={16} /> Original
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <Empty
          text={
            !selectedEventId
              ? "Select an event to load its photographs."
              : query || favoritesOnly
                ? "No loaded photographs match these filters."
                : "This gallery has no available photographs yet."
          }
        />
      )}
      {hasMore && (
        <button
          type="button"
          className="px-gallery-load-more"
          disabled={loadingMore}
          onClick={onLoadMore}
        >
          {loadingMore ? (
            <LoaderCircle className="animate-spin" size={17} />
          ) : (
            <Images size={17} />
          )}{" "}
          {loadingMore ? "Loading photographs…" : "Load more photographs"}
        </button>
      )}
    </section>
  );
}
function Empty({ text }) {
  return (
    <div className="rounded-3xl border border-dashed border-[#d8d0c7] bg-paper p-12 text-center text-sm text-slate-500">
      {text}
    </div>
  );
}
function ErrorNotice({ message }) {
  return (
    <div
      className="mb-5 rounded-xl bg-red-50 p-4 text-sm text-red-800"
      role="alert"
    >
      {message}
    </div>
  );
}
function Status({ icon: Icon, spin, title, message, action }) {
  return (
    <main className="grid min-h-screen place-items-center bg-canvas p-6 text-center">
      <div>
        <Icon className={spin ? "mx-auto animate-spin" : "mx-auto"} size={34} />
        <h1 className="mt-4 text-2xl font-extrabold">{title}</h1>
        {message && (
          <p className="max-w-lg text-sm text-slate-500">{message}</p>
        )}
        {action && (
          <button
            onClick={action}
            className="mt-4 rounded-xl bg-ink px-5 py-3 text-xs font-bold text-white"
          >
            Retry
          </button>
        )}
      </div>
    </main>
  );
}
function formatDate(value) {
  if (!value) return "not available";
  return new Intl.DateTimeFormat("en-IN", { dateStyle: "medium" }).format(
    new Date(value),
  );
}
function formatBytes(value) {
  const bytes = Number(value || 0);
  if (!bytes) return "0 GB";
  return `${(bytes / 1073741824).toFixed(bytes >= 10737418240 ? 0 : 1)} GB`;
}
function formatMoney(amountMinor, currency) {
  return new Intl.NumberFormat("en-IN", { style: "currency", currency }).format(
    amountMinor / 100,
  );
}

export default App;
